import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Reveal } from "./primitives.jsx";

// The six featured appearances — the ones that look and sound the best, each
// with a real on-camera thumbnail so visitors can see what they're getting.
// All are YouTube (reliable, attractive stills + inline playback); the audio-
// only shows are sat out here because their platforms expose no usable artwork.
//
// `hd: true` means a crisp maxresdefault thumbnail exists; otherwise we fall
// back to hqdefault (always present). `pos` tunes object-position so the crop
// keeps Greg's face / the important part in frame. `start` (seconds) skips a
// clip's dead air — applied to both the embed and the watch-on-YouTube link.
const EPISODES = [
  {
    id: "htown-wheelhouse",
    yt: "QNIPcsQLiRU",
    hd: false,
    pos: "center 26%",
    feature: true,
    title: "World Series, Brett & Old-School Ball",
    show: "H-Town Wheelhouse",
    note: "A 1985 World Series champ on the ring, old school vs. new school, and a few George Brett stories you won't hear anywhere else.",
  },
  {
    id: "hof-author-series",
    yt: "SOsnIesVhbE",
    hd: false,
    pos: "center",
    feature: true,
    title: "Virtual Author Series",
    show: "Baseball Hall of Fame",
    note: "Cooperstown sits Greg down for the long version — the book, ten seasons in the bigs, Disco Demolition Night, and the Pine Tar Game.",
  },
  {
    id: "fsc-clubhouse",
    yt: "5EJHRN5npZ4",
    hd: true,
    pos: "center",
    start: 16,
    title: "Back in the Mocs' Clubhouse",
    show: "Florida Southern Baseball",
    note: "Greg returns to his alma mater — where he was a two-time All-American — to talk with the Mocs in their clubhouse before a February practice.",
  },
  {
    id: "eagle-new-book",
    yt: "xEOLbygc2lg",
    hd: true,
    pos: "center 40%",
    title: "The New Book",
    show: "96.7 The Eagle",
    host: "Double T",
    note: 'Greg tells Double T about "The Day the Yankees Made Me Shave" — where the title came from and why he finally wrote it down.',
  },
  {
    id: "eagle-chicago-preview",
    yt: "VyhpKEIO7QA",
    hd: true,
    pos: "center 22%",
    title: "2021 Chicago Baseball Preview",
    show: "96.7 The Eagle",
    host: "Double T",
    note: "Opening Day, what he'd change about today's game, Tony LaRussa, and — of course — Disco Demolition Night.",
  },
  {
    id: "umpire-stories",
    yt: "IaCgL7CvPlM",
    hd: true,
    pos: "center 32%",
    title: "Umpire Stories",
    show: "Myosource Kinetic Bands",
    note: "The men in blue, up close — the arguments, the respect, and the calls Greg still remembers.",
  },
];

const ytEmbed = (id, start) =>
  `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1${start ? `&start=${start}` : ""}`;
const ytWatch = (id, start) => `https://youtu.be/${id}${start ? `?t=${start}` : ""}`;
const thumbMax = (id) => `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
const thumbHq = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

// Thumbnail with graceful fallback: prefer the crisp maxres still when it
// exists, drop to hqdefault (always present) on error, and finally a tuned-out
// "snow" plate if even that fails, so a card never shows a broken image.
function Thumb({ ep }) {
  const [stage, setStage] = useState(ep.hd ? "max" : "hq");
  if (stage === "fail") return <div className="pods-card-snow" aria-hidden />;
  const src = stage === "max" ? thumbMax(ep.yt) : thumbHq(ep.yt);
  return (
    <img
      className="pods-card-img"
      src={src}
      alt={`${ep.title} — ${ep.show}`}
      style={{ objectPosition: ep.pos }}
      loading="lazy"
      decoding="async"
      onError={() => setStage((s) => (s === "max" ? "hq" : "fail"))}
    />
  );
}

export default function Podcasts() {
  // The episode playing in the lightbox, or null when closed.
  const [active, setActive] = useState(null);
  const closeRef = useRef(null);

  // Same modal contract as the Game Tape lightbox: Escape closes, body scroll
  // locks while open, focus lands on the close button.
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const t = requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      cancelAnimationFrame(t);
    };
  }, [active]);

  return (
    <section id="podcasts" data-screen-label="On the Air" className="section pods">
      <div className="section-inner">
        <div className="pods-head">
          <Reveal>
            <div className="pods-eyebrow mono">
              <span className="pods-on-air" aria-hidden />
              PODCASTS · RADIO · INTERVIEWS
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="display h-lg pods-title">
              On the <em>air.</em>
            </h2>
          </Reveal>
          <Reveal delay={240}>
            <p className="lede pods-lede">
              I've never turned down a microphone. Here are a few of the best — pull up a chair, hit
              play, and listen in.
            </p>
          </Reveal>
        </div>

        <div className="pods-card-grid">
          {EPISODES.map((ep, i) => (
            <Reveal
              key={ep.id}
              delay={Math.min(i * 80, 360)}
              as="button"
              className={`pods-card ${ep.feature ? "pods-card-feature" : ""}`}
              onClick={() => setActive(ep)}
              aria-label={`Play ${ep.title} on ${ep.show}`}
            >
              <span className="pods-card-thumb">
                <Thumb ep={ep} />
                <span className="pods-card-shade" aria-hidden />
                <span className="pods-card-chip mono">{ep.platform || "YouTube"}</span>
                <span className="pods-card-play" aria-hidden>
                  <span className="pods-card-play-tri" />
                </span>
              </span>
              <span className="pods-card-meta">
                <span className="pods-card-title">{ep.title}</span>
                <span className="pods-card-show mono">
                  {ep.show}
                  {ep.host ? ` · ${ep.host}` : ""}
                </span>
              </span>
            </Reveal>
          ))}
        </div>
      </div>

      {active &&
        createPortal(
          <div
            className="pod-modal"
            onClick={() => setActive(null)}
            role="dialog"
            aria-modal="true"
            aria-label={`${active.title} — ${active.show}`}
          >
            <div className="pod-modal-card" onClick={(e) => e.stopPropagation()}>
              <button
                ref={closeRef}
                className="pod-modal-close"
                onClick={() => setActive(null)}
                aria-label="Close player"
              >
                ×
              </button>
              <div className="pod-modal-chrome mono">
                <span className="pods-on-air" aria-hidden />
                <span>ON THE AIR</span>
                <span className="pod-modal-chrome-meta">{active.show}</span>
              </div>

              <div className="pod-modal-screen">
                <iframe
                  src={ytEmbed(active.yt, active.start)}
                  title={`${active.title} — ${active.show}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>

              <div className="pod-modal-foot">
                <div className="pod-modal-foot-text">
                  <span className="pod-modal-title">{active.title}</span>
                  <span className="pod-modal-show mono">
                    {active.show}
                    {active.host ? ` · ${active.host}` : ""}
                  </span>
                  <span className="pod-modal-note">{active.note}</span>
                </div>
                <a
                  className="pod-modal-link mono"
                  href={ytWatch(active.yt, active.start)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Watch on YouTube ↗
                </a>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
}

const podStyles = document.createElement("style");
podStyles.textContent = `
.pods { background: var(--navy-900); }
.pods-head { margin-bottom: 34px; }
.pods-title { color: var(--bone); margin: 14px 0 22px; }
.pods-title em { color: var(--gold); font-style: italic; }
.pods-lede { max-width: 56ch; }

/* Eyebrow + steady-red broadcast "on air" light */
.pods-eyebrow {
  display: inline-flex; align-items: center; gap: 10px;
  font-size: 0.6875rem; font-weight: 500; letter-spacing: 0.2em;
  text-transform: uppercase; color: var(--gold);
}
.pods-on-air {
  width: 8px; height: 8px; border-radius: 50%;
  background: var(--crimson);
  box-shadow: 0 0 10px rgba(200,16,46,0.85);
  animation: podsBlink 2s steps(1, end) infinite;
  flex: 0 0 auto;
}
@keyframes podsBlink { 0%, 70% { opacity: 1; } 71%, 100% { opacity: 0.25; } }

/* Featured layout — two larger cards up top, four beneath. A 12-track grid
   lets the top two span half each and the bottom four span a quarter each. */
.pods-card-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 18px;
}
.pods-card-feature { grid-column: span 6; }
.pods-card:not(.pods-card-feature) { grid-column: span 3; }

/* Card — a clickable plate: thumbnail on top, title + show beneath */
.pods-card {
  display: flex; flex-direction: column;
  padding: 0; border: 1px solid var(--rule);
  background: #060f1d;
  text-align: left; cursor: pointer; font-family: inherit;
  overflow: hidden;
  transition: border-color 300ms, transform 300ms cubic-bezier(.2,.8,.2,1), box-shadow 300ms;
}
.pods-card:hover,
.pods-card:focus-visible {
  transform: translateY(-4px);
  border-color: var(--royal-blue-bright);
  box-shadow: 0 30px 60px -26px rgba(0, 70, 173, 0.6);
  outline: none;
}
.pods-card:focus-visible { box-shadow: 0 0 0 2px var(--royal-blue-glow), 0 30px 60px -26px rgba(0,70,173,0.6); }

.pods-card-thumb {
  position: relative; display: block;
  aspect-ratio: 16 / 9; overflow: hidden; background: #060f1d;
}
.pods-card-img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover; object-position: center;
  filter: saturate(0.9) contrast(1.04) brightness(0.92);
  transition: transform 700ms cubic-bezier(.2,.8,.2,1), filter 400ms;
}
.pods-card:hover .pods-card-img,
.pods-card:focus-visible .pods-card-img {
  transform: scale(1.06);
  filter: saturate(1) contrast(1.04) brightness(1);
}
.pods-card-shade {
  position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(180deg, rgba(5,9,18,0.25) 0%, transparent 28%, transparent 62%, rgba(5,9,18,0.72) 100%);
}
/* Tuned-out "snow" if a thumbnail ever fails to load */
.pods-card-snow {
  position: absolute; inset: 0; background-color: #0a1525;
  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxNjAnIGhlaWdodD0nMTYwJz48ZmlsdGVyIGlkPSduJz48ZmVUdXJidWxlbmNlIHR5cGU9J2ZyYWN0YWxOb2lzZScgYmFzZUZyZXF1ZW5jeT0nMC44NScgbnVtT2N0YXZlcz0nMicgc3RpdGNoVGlsZXM9J3N0aXRjaCcvPjxmZUNvbG9yTWF0cml4IHZhbHVlcz0nMCAwIDAgMCAwICAwIDAgMCAwIDAgIDAgMCAwIDAgMCAgMCAwIDAgMC40MiAwJy8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9JzEwMCUnIGhlaWdodD0nMTAwJScgZmlsdGVyPSd1cmwoI24pJy8+PC9zdmc+");
  opacity: 0.5;
}
.pods-card-chip {
  position: absolute; top: 12px; left: 12px; z-index: 3;
  font-size: 0.5625rem; letter-spacing: 0.18em; color: var(--navy-900);
  background: var(--gold); padding: 4px 8px;
}
.pods-card-play {
  position: absolute; z-index: 3;
  top: 50%; left: 50%; transform: translate(-50%, -50%) scale(0.92);
  width: 58px; height: 58px; border-radius: 50%;
  background: rgba(189,155,96,0.94);
  display: grid; place-items: center;
  box-shadow: 0 16px 40px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(244,241,234,0.15) inset;
  opacity: 0.85;
  transition: transform 300ms cubic-bezier(.2,.8,.2,1), background 220ms, opacity 220ms;
}
.pods-card-feature .pods-card-play { width: 70px; height: 70px; }
.pods-card:hover .pods-card-play,
.pods-card:focus-visible .pods-card-play {
  transform: translate(-50%, -50%) scale(1);
  background: var(--gold-bright); opacity: 1;
}
.pods-card-play-tri {
  width: 0; height: 0; margin-left: 4px;
  border-style: solid; border-width: 9px 0 9px 15px;
  border-color: transparent transparent transparent var(--navy-900);
}
.pods-card-feature .pods-card-play-tri { border-width: 11px 0 11px 18px; }

.pods-card-meta {
  display: flex; flex-direction: column; gap: 6px;
  padding: 16px 18px;
  border-top: 1px solid var(--rule);
  background: rgba(5, 13, 26, 0.5);
}
.pods-card-title {
  font-family: var(--serif); font-weight: 500; line-height: 1.18;
  font-size: 1rem; color: var(--bone);
}
.pods-card-feature .pods-card-title { font-size: clamp(1.1875rem, 1.6vw, 1.5rem); }
.pods-card-show { font-size: 0.625rem; letter-spacing: 0.12em; color: var(--gold); }

@media (max-width: 900px) {
  .pods-card-grid { grid-template-columns: repeat(2, 1fr); gap: 14px; }
  .pods-card-feature, .pods-card:not(.pods-card-feature) { grid-column: span 1; }
  .pods-card-feature .pods-card-title { font-size: 1.0625rem; }
}
@media (max-width: 560px) {
  .pods-card-grid { grid-template-columns: 1fr; gap: 14px; }
  .pods-card-play { width: 52px; height: 52px; opacity: 1; }
}

/* ---- Lightbox ---- */
.pod-modal {
  position: fixed; inset: 0; z-index: 200;
  background: rgba(5, 13, 26, 0.9);
  backdrop-filter: blur(10px);
  display: flex; align-items: center; justify-content: center;
  padding: 32px;
  animation: podFadeIn 240ms;
}
@keyframes podFadeIn { from { opacity: 0; } to { opacity: 1; } }
.pod-modal-card {
  position: relative; width: 100%; max-width: 900px;
  background: var(--navy-800);
  border: 1px solid var(--royal-blue-bright);
  box-shadow: 0 40px 100px -20px rgba(0,0,0,0.78), 0 0 0 1px rgba(0,70,173,0.4);
  animation: podScaleIn 300ms cubic-bezier(.2,.8,.2,1);
  overflow: hidden;
}
@keyframes podScaleIn { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: scale(1); } }
.pod-modal-close {
  position: absolute; top: 12px; right: 12px; z-index: 6;
  width: 38px; height: 38px; border-radius: 50%;
  background: rgba(5,13,26,0.85); border: 1px solid var(--rule);
  color: var(--bone); font-size: 1.375rem; line-height: 1; cursor: pointer;
  transition: background 200ms, border-color 200ms;
}
.pod-modal-close:hover { background: var(--royal-blue-bright); border-color: var(--royal-blue-bright); }
.pod-modal-chrome {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px; border-bottom: 1px solid var(--rule);
  font-size: 0.625rem; letter-spacing: 0.16em; color: var(--gold);
}
.pod-modal-chrome-meta { margin-left: auto; color: var(--bone-dim); padding-right: 40px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pod-modal-screen { position: relative; width: 100%; aspect-ratio: 16 / 9; background: #000; }
.pod-modal-screen iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.pod-modal-foot {
  display: flex; align-items: center; justify-content: space-between;
  gap: 20px; flex-wrap: wrap; padding: 20px 22px;
  border-top: 1px solid var(--rule);
}
.pod-modal-foot-text { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.pod-modal-title { font-family: var(--serif); font-size: 1.25rem; font-weight: 500; color: var(--bone); }
.pod-modal-show { font-size: 0.625rem; letter-spacing: 0.12em; color: var(--gold); }
.pod-modal-note { font-size: 0.8125rem; line-height: 1.55; color: var(--bone-dim); max-width: 52ch; margin-top: 2px; }
.pod-modal-link {
  flex: 0 0 auto; display: inline-flex; align-items: center;
  font-size: 0.6875rem; letter-spacing: 0.14em; color: var(--gold);
  border: 1px solid var(--rule-strong); padding: 10px 16px; white-space: nowrap;
  transition: background 200ms, color 200ms, border-color 200ms;
}
.pod-modal-link:hover { background: var(--gold); color: var(--navy-900); border-color: var(--gold); }

@media (max-width: 560px) {
  .pod-modal { padding: 14px; }
  .pod-modal-chrome { gap: 8px; font-size: 0.5625rem; padding: 12px; }
  .pod-modal-chrome-meta { padding-right: 46px; }
  .pod-modal-foot { padding: 16px; gap: 14px; }
  .pod-modal-note { font-size: 0.75rem; }
  .pod-modal-link { width: 100%; justify-content: center; }
}
`;
document.head.appendChild(podStyles);
